import type { EditorView } from "@codemirror/view";
import { getDocumentAssetsDir, parseMagicMd } from "@magic-md/core";
import { t as translate } from "@magic-md/i18n";
import type {
    SmartIntakeBundle,
    SmartIntakeContentItem,
    SmartIntakeOffer,
    SmartIntakeOperation,
    SmartIntakePlan,
    SmartIntakeSelection,
    SmartIntakeSource,
    JSONSchema,
} from "@magic-md/plugin-sdk";
import { pluginUIRegistry } from "../plugins/plugin-ui-registry";
import { getPlatformService } from "../platform";
import { useEditorStore } from "../store/editor-store";
import { updateDefinitionConfigAndDropOutput } from "./unit-edit-target";

interface IntakeContext {
    bundle: SmartIntakeBundle;
    view: EditorView;
}

interface ResolvedPlanState {
    assetRefs: Map<string, string>;
    createdDefinitions: Map<string, string>;
    markdownParts: string[];
    openConfigDefinitionId: string | null;
}

type SmartIntakeOfferProviderKind = "builtin" | "ui" | "runtime";
type SmartIntakeFileItem = Extract<SmartIntakeContentItem, { kind: "file" }>;
type SmartIntakeFileItemWithBlob = SmartIntakeFileItem & { blob: Blob };
type SmartIntakeAssetItem = Extract<SmartIntakeContentItem, { kind: "file" | "folder" }>;

interface BrowserFileSystemEntry {
    name: string;
    isFile: boolean;
    isDirectory: boolean;
}

interface BrowserFileSystemFileEntry extends BrowserFileSystemEntry {
    file(callback: (file: File) => void, errorCallback?: (error: unknown) => void): void;
}

interface BrowserFileSystemDirectoryEntry extends BrowserFileSystemEntry {
    createReader(): {
        readEntries(
            callback: (entries: BrowserFileSystemEntry[]) => void,
            errorCallback?: (error: unknown) => void,
        ): void;
    };
}

interface DataTransferItemWithEntry {
    webkitGetAsEntry?: () => BrowserFileSystemEntry | null;
}

type ResolvedSmartIntakeOffer = SmartIntakeOffer & {
    providerKind: SmartIntakeOfferProviderKind;
    pluginTitle?: string;
};

function schemaHasType(schema: JSONSchema | undefined, type: string): boolean {
    if (!schema) return false;
    return Array.isArray(schema.type)
        ? schema.type.includes(type)
        : schema.type === type;
}

export interface SmartIntakeNormalizeInput {
    source: SmartIntakeSource;
    data: DataTransfer;
    documentPath: string | null;
    selection: SmartIntakeSelection;
}

export interface SmartIntakeFilePathNormalizeInput {
    source: SmartIntakeSource;
    paths: string[];
    documentPath: string | null;
    selection: SmartIntakeSelection;
}

export interface SmartIntakeRuntimePreparation {
    bundle: SmartIntakeBundle;
    cleanup?: () => Promise<void>;
}

export interface SmartIntakeCommitAssetInput {
    bundle: SmartIntakeBundle;
    operation: Extract<SmartIntakeOperation, { type: "commitAsset" }>;
}

export interface SmartIntakeExternalPathCopyInput {
    from: string;
    to: string;
}

export interface SmartIntakePlatform {
    id: string;
    canHandleDataTransfer(data: DataTransfer | null): boolean;
    normalizeDataTransfer(input: SmartIntakeNormalizeInput): Promise<SmartIntakeBundle>;
    normalizeFilePaths?(input: SmartIntakeFilePathNormalizeInput): Promise<SmartIntakeBundle>;
    prepareRuntimeBundle?(bundle: SmartIntakeBundle): Promise<SmartIntakeRuntimePreparation>;
    copyExternalPathToWorkspace?(input: SmartIntakeExternalPathCopyInput): Promise<void>;
    commitAsset(input: SmartIntakeCommitAssetInput): Promise<string>;
    chooseOffer?(offers: ResolvedSmartIntakeOffer[]): Promise<ResolvedSmartIntakeOffer | null>;
    requestOptions?(offer: ResolvedSmartIntakeOffer): Promise<unknown | null>;
}

const IMAGE_EXTENSIONS = new Set(["png", "jpg", "jpeg", "gif", "webp", "avif", "svg"]);
const IMAGE_MIME_RE = /^image\//i;
const DATA_IMAGE_RE = /^data:(image\/[a-z0-9.+-]+);(?:[^,]*;)?base64,([a-z0-9+/=\s]+)$/i;
const DATA_URL_RE = /data:image\/[a-z0-9.+-]+;[^"'()\s<>]*base64,[a-z0-9+/=\s]+/gi;
const SCHEME_URL_RE = /[A-Za-z][A-Za-z0-9+.-]*:\/\/[^\s<>"'`]+/i;
const MAX_STRUCTURED_TEXT_FILE_BYTES = 2 * 1024 * 1024;

let smartIntakePlatform: SmartIntakePlatform | null = null;

function tr(key: string, params?: Record<string, unknown>): string {
    return translate("editor", key, params);
}

export function setSmartIntakePlatform(platform: SmartIntakePlatform | null): void {
    smartIntakePlatform = platform;
}

export function getSmartIntakePlatform(): SmartIntakePlatform {
    return smartIntakePlatform ?? defaultSmartIntakePlatform;
}

export function hasSmartIntakeData(data: DataTransfer | null): boolean {
    return getSmartIntakePlatform().canHandleDataTransfer(data);
}

export async function handleSmartIntakeFromDataTransfer(
    source: SmartIntakeSource,
    data: DataTransfer,
    view: EditorView,
    documentPath: string | null,
): Promise<boolean> {
    const selection = view.state.selection.main;
    const selectedText =
        selection.from === selection.to
            ? ""
            : view.state.doc.sliceString(selection.from, selection.to);
    const platform = getSmartIntakePlatform();
    const bundle = await platform.normalizeDataTransfer({
        source,
        data,
        documentPath,
        selection: { from: selection.from, to: selection.to, text: selectedText },
    });

    return handleSmartIntakeNormalizedBundle(bundle, view);
}

export async function handleSmartIntakeFromFilePaths(
    source: SmartIntakeSource,
    paths: string[],
    view: EditorView,
    documentPath: string | null,
): Promise<boolean> {
    const platform = getSmartIntakePlatform();
    if (!platform.normalizeFilePaths || paths.length === 0) return false;

    const selection = view.state.selection.main;
    const selectedText =
        selection.from === selection.to
            ? ""
            : view.state.doc.sliceString(selection.from, selection.to);
    const bundle = await platform.normalizeFilePaths({
        source,
        paths,
        documentPath,
        selection: { from: selection.from, to: selection.to, text: selectedText },
    });

    return handleSmartIntakeNormalizedBundle(bundle, view);
}

async function handleSmartIntakeNormalizedBundle(
    bundle: SmartIntakeBundle,
    view: EditorView,
): Promise<boolean> {
    if (bundle.items.length === 0) return false;
    if (!bundle.documentPath && bundle.items.some(isAssetItem)) {
        const inlineMarkdown = await buildInlineImageFallbackMarkdown(bundle);
        if (inlineMarkdown) {
            insertMarkdown(view, inlineMarkdown);
            useEditorStore.getState().setStatusMessage(
                tr("smartIntake.status.inlineImageFallback"),
                3500,
            );
            return true;
        }
        const inlineItems = bundle.items.filter((item) => !isAssetItem(item));
        if (inlineItems.length > 0) {
            return handleSmartIntakeBundle({
                bundle: {
                    ...bundle,
                    items: inlineItems,
                },
                view,
            });
        }
        if (hasRuntimeAddressableAsset(bundle) && getPlatformService().engine.smartIntakePropose) {
            return handleSmartIntakeBundle({ bundle, view });
        }
        useEditorStore.getState().setStatusMessage(tr("smartIntake.status.saveBeforeImport"), 3500);
        return false;
    }

    return handleSmartIntakeBundle({ bundle, view });
}

async function handleSmartIntakeBundle(ctx: IntakeContext): Promise<boolean> {
    const platform = getSmartIntakePlatform();
    let runtimePreparation: SmartIntakeRuntimePreparation | null = null;

    try {
        if (platform.prepareRuntimeBundle && getPlatformService().engine.smartIntakePropose) {
            runtimePreparation = await platform.prepareRuntimeBundle(ctx.bundle);
        }

        const defaultOffer = buildDefaultOffer(ctx.bundle);
        const uiOffers = await collectPluginOffers(ctx.bundle);
        const runtimeOffers = runtimePreparation
            ? await collectRuntimeOffers(runtimePreparation.bundle)
            : [];
        const offers = dedupeSmartIntakeOffers(
            [defaultOffer, ...uiOffers, ...runtimeOffers].sort(
                (a, b) => b.confidence - a.confidence,
            ),
        );
        const selectedOffer = await (platform.chooseOffer?.(offers) ?? chooseSmartIntakeOffer(offers));
        if (!selectedOffer) return false;

        const options = selectedOffer.needsInput
            ? await (platform.requestOptions?.(selectedOffer) ?? requestSmartIntakeOptions(selectedOffer))
            : undefined;
        if (selectedOffer.needsInput && options === null) return false;

        const planBundle =
            selectedOffer.providerKind === "runtime" && runtimePreparation
                ? runtimePreparation.bundle
                : ctx.bundle;
        const plan = await buildPlanForOffer(planBundle, selectedOffer, options);
        await applySmartIntakePlan({ bundle: planBundle, view: ctx.view }, plan);
        useEditorStore.getState().setStatusMessage(tr("smartIntake.status.applied"), 2000);
        return true;
    } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        useEditorStore.getState().setStatusMessage(
            tr("smartIntake.status.failed", { message }),
            5000,
        );
        return false;
    } finally {
        await runtimePreparation?.cleanup?.().catch(() => {});
    }
}

export function createDefaultSmartIntakePlatform(
    overrides: Partial<SmartIntakePlatform> = {},
): SmartIntakePlatform {
    return {
        id: "browser",
        canHandleDataTransfer(data) {
            if (!data) return false;
            if (data.files && data.files.length > 0) return true;
            for (const item of Array.from(data.items ?? [])) {
                if (item.kind === "file") return true;
            }
            const html = safeGetData(data, "text/html");
            if (html.trim()) return true;
            const uri = safeGetData(data, "text/uri-list");
            if (uri.trim()) return true;
            const markdown = safeGetData(data, "text/markdown");
            if (markdown.trim()) return true;
            const text = safeGetData(data, "text/plain").trim();
            return (
                looksLikeMagicFragment(text) ||
                looksLikeStructuredJsonText(text) ||
                looksLikeTabularText(text) ||
                looksLikeStandaloneUrl(text) ||
                !!parseMarkdownUrl(text) ||
                containsCustomSchemeUrl(text) ||
                containsDataUrlImage(text)
            );
        },
        normalizeDataTransfer: normalizeBrowserDataTransfer,
        prepareRuntimeBundle: prepareBrowserRuntimeBundle,
        commitAsset: commitAssetToWorkspace,
        chooseOffer: chooseSmartIntakeOffer,
        requestOptions: requestSmartIntakeOptions,
        ...overrides,
    };
}

const defaultSmartIntakePlatform = createDefaultSmartIntakePlatform();

async function normalizeBrowserDataTransfer({
    source,
    data,
    documentPath,
    selection,
}: SmartIntakeNormalizeInput): Promise<SmartIntakeBundle> {
    const items: SmartIntakeContentItem[] = [];
    const seenFiles = new Set<Blob>();
    const seenFileSignatures = new Set<string>();
    const structuredFileCandidates: Array<{
        file: File;
        name: string;
        mime?: string;
        sourceItemId: string;
    }> = [];
    let index = 0;

    const addFile = (file: File, nameOverride?: string) => {
        const name = nameOverride || file.webkitRelativePath || file.name || defaultFileName(file);
        const signature = buildFileSignature(file, name);
        if (seenFiles.has(file) || seenFileSignatures.has(signature)) return;
        seenFiles.add(file);
        seenFileSignatures.add(signature);
        const id = `file-${++index}`;
        const mime = file.type || undefined;
        items.push({
            id,
            kind: "file",
            name,
            mime,
            size: file.size,
            blob: file,
        });
        if (shouldReadStructuredTextFile(name, mime, file.size)) {
            structuredFileCandidates.push({ file, name, mime, sourceItemId: id });
        }
    };

    const addBlobFile = (blob: Blob, name: string, mime?: string) => {
        if (seenFiles.has(blob)) return;
        seenFiles.add(blob);
        items.push({
            id: `file-${++index}`,
            kind: "file",
            name,
            mime: mime || blob.type || undefined,
            size: blob.size,
            blob,
        });
    };

    for (const file of Array.from(data.files ?? [])) addFile(file);
    for (const item of Array.from(data.items ?? [])) {
        if (item.kind !== "file") continue;
        const entry = getDataTransferEntry(item);
        if (entry?.isDirectory) {
            const files = await readDirectoryEntryFiles(entry, entry.name);
            for (const file of files) addFile(file.file, file.relativePath);
            continue;
        }
        const file = item.getAsFile();
        if (file) addFile(file);
    }

    for (const candidate of structuredFileCandidates) {
        const text = await candidate.file.text().catch(() => "");
        addStructuredTextItem(items, () => ++index, {
            text,
            name: candidate.name,
            mime: candidate.mime,
            sourceItemId: candidate.sourceItemId,
        });
    }

    const html = safeGetData(data, "text/html").trim();
    if (html) {
        for (const image of extractDataUrlImagesFromHtml(html)) {
            addBlobFile(image.blob, image.name, image.mime);
        }

        const htmlWithoutDataImages = removeHtmlNodes(html, (element) => {
            return element.tagName.toLowerCase() === "img" && isDataUrl(element.getAttribute("src") ?? "");
        });
        for (const table of extractHtmlTableItems(htmlWithoutDataImages)) {
            items.push({
                id: `table-${++index}`,
                kind: "table",
                format: "html",
                text: table,
                ...tableRowsFromText(table, "html"),
            });
        }
        const htmlWithoutTables = removeHtmlNodes(
            htmlWithoutDataImages,
            (element) => element.tagName.toLowerCase() === "table",
        );
        if (htmlHasMeaningfulContent(htmlWithoutTables)) {
            items.push({
                id: `html-${++index}`,
                kind: "html",
                html: htmlWithoutTables,
                text: safeGetData(data, "text/plain") || undefined,
            });
        }
    }

    const plain = safeGetData(data, "text/plain").trim();
    for (const image of extractDataUrlImagesFromText(plain)) {
        addBlobFile(image.blob, image.name, image.mime);
    }

    const uriLines = safeGetData(data, "text/uri-list")
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter((line) => line && !line.startsWith("#"));
    for (const uri of uriLines) {
        items.push({ id: `url-${++index}`, kind: "url", url: uri });
    }

    const markdown = safeGetData(data, "text/markdown").trim();
    if (markdown) {
        items.push({ id: `text-${++index}`, kind: "text", format: "markdown", text: markdown });
    } else if (looksLikeMagicFragment(plain)) {
        items.push({ id: `magic-${++index}`, kind: "magic-fragment", markdown: plain });
    } else {
        const addedStructured = items.some((item) => item.kind === "json" || item.kind === "har")
            ? false
            : addStructuredTextItem(items, () => ++index, { text: plain });
        if (!addedStructured) {
            addPlainTextIntakeItem({
                items,
                plain,
                uriLines,
                nextIndex: () => ++index,
            });
        }
    }

    return {
        source,
        documentPath: documentPath ?? "",
        documentBundlePath: documentPath ? documentPathToBundlePath(documentPath) : null,
        selection,
        items,
    };
}

async function prepareBrowserRuntimeBundle(
    bundle: SmartIntakeBundle,
): Promise<SmartIntakeRuntimePreparation> {
    if (!bundle.documentPath) return { bundle: stripRuntimeOnlyPayload(bundle) };

    const files = bundle.items.filter(
        (item): item is SmartIntakeFileItemWithBlob =>
            item.kind === "file" && !!item.blob && !item.stagedPath,
    );
    if (files.length === 0) return { bundle: stripRuntimeOnlyPayload(bundle) };

    const assetsDir = getDocumentAssetsDir(bundle.documentPath);
    const stagingRoot = `${assetsDir}/.intake/${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const stagedItems = new Map<string, string>();

    for (const file of files) {
        const stagedPath = `${stagingRoot}/${sanitizeFileName(file.name || defaultFileName(file.blob))}`;
        const content = await blobToBase64(file.blob);
        const result = await getPlatformService().engine.workspaceWriteFile({
            path: stagedPath,
            content,
            encoding: "base64",
            createParents: true,
        });
        if (!result.success) throw new Error(tr("smartIntake.error.stageAssetFailed", { name: file.name }));
        stagedItems.set(file.id, stagedPath);
    }

    return {
        bundle: {
            ...bundle,
            items: bundle.items.map((item) => {
                if (item.kind !== "file") return item;
                const { blob: _blob, ...rest } = item;
                return {
                    ...rest,
                    ...(stagedItems.has(item.id) ? { stagedPath: stagedItems.get(item.id) } : {}),
                };
            }),
        },
        cleanup: async () => {
            await getPlatformService().engine.workspaceRemove({
                path: stagingRoot,
                recursive: true,
            });
        },
    };
}

function stripRuntimeOnlyPayload(bundle: SmartIntakeBundle): SmartIntakeBundle {
    return {
        ...bundle,
        items: bundle.items.map((item) => {
            if (item.kind !== "file") return item;
            const { blob: _blob, ...rest } = item;
            return rest;
        }),
    };
}

function buildDefaultOffer(bundle: SmartIntakeBundle): ResolvedSmartIntakeOffer {
    const assetCount = bundle.items.filter(isAssetItem).length;
    const tableCount = bundle.items.filter((item) => item.kind === "table").length;
    return {
        id: "builtin:insert-markdown",
        providerKind: "builtin",
        title:
            assetCount > 0
                ? tr("smartIntake.offer.insertAssets")
                : tableCount > 0
                    ? tr("smartIntake.offer.insertTable")
                    : tr("smartIntake.offer.insertMarkdown"),
        description:
            assetCount > 0
                ? tr("smartIntake.offer.insertAssets.description")
                : tr("smartIntake.offer.insertMarkdown.description"),
        icon: "↵",
        confidence: 50,
        consumes: bundle.items.map((item) => item.id),
    };
}

async function collectPluginOffers(bundle: SmartIntakeBundle): Promise<ResolvedSmartIntakeOffer[]> {
    const offers: ResolvedSmartIntakeOffer[] = [];
    for (const [pluginName, plugin] of pluginUIRegistry.getAll()) {
        const provider = plugin.smartIntake;
        if (!provider) continue;
        try {
            const result = await provider.propose({ bundle, pluginName });
            for (const offer of result) {
                offers.push({
                    ...offer,
                    providerKind: "ui",
                    plugin: offer.plugin ?? pluginName,
                    pluginTitle: offer.pluginTitle ?? plugin.manifest.title,
                    icon: offer.icon ?? plugin.manifest.icon,
                });
            }
        } catch {
            // One plugin should not disable built-in paste/drop handling.
        }
    }
    return offers;
}

async function collectRuntimeOffers(
    bundle: SmartIntakeBundle,
): Promise<ResolvedSmartIntakeOffer[]> {
    const engine = getPlatformService().engine;
    if (!engine.smartIntakePropose) return [];
    try {
        const result = await engine.smartIntakePropose({ bundle });
        return result.offers.map((offer: SmartIntakeOffer) => ({
            ...offer,
            providerKind: "runtime",
            pluginTitle: offer.pluginTitle ?? (offer.plugin
                ? pluginUIRegistry.get(offer.plugin)?.manifest.title
                : undefined),
        }));
    } catch {
        return [];
    }
}

function dedupeSmartIntakeOffers(
    offers: ResolvedSmartIntakeOffer[],
): ResolvedSmartIntakeOffer[] {
    const seen = new Set<string>();
    const deduped: ResolvedSmartIntakeOffer[] = [];
    for (const offer of offers) {
        const key = smartIntakeOfferDedupeKey(offer);
        if (key && seen.has(key)) continue;
        if (key) seen.add(key);
        deduped.push(offer);
    }
    return deduped;
}

function smartIntakeOfferDedupeKey(offer: ResolvedSmartIntakeOffer): string | null {
    const plugin = offer.plugin?.trim();
    if (!plugin) return null;
    return JSON.stringify({
        plugin,
        title: normalizeDedupeText(offer.title),
        description: normalizeDedupeText(offer.description ?? ""),
        consumes: [...offer.consumes].sort(),
        needsInput: !!offer.needsInput,
        inputSchema: offer.inputSchema ?? null,
        previewMarkdown: normalizeDedupeText(offer.previewMarkdown ?? ""),
    });
}

function normalizeDedupeText(value: string): string {
    return value.trim().replace(/\s+/g, " ");
}

async function buildPlanForOffer(
    bundle: SmartIntakeBundle,
    offer: ResolvedSmartIntakeOffer,
    options?: unknown,
): Promise<SmartIntakePlan> {
    if (offer.id === "builtin:insert-markdown") return buildDefaultPlan(bundle);
    if (!offer.plugin) return buildDefaultPlan(bundle);
    if (offer.providerKind === "runtime") {
        const engine = getPlatformService().engine;
        if (!engine.smartIntakeBuildPlan) return buildDefaultPlan(bundle);
        const result = await engine.smartIntakeBuildPlan({
            bundle,
            pluginName: offer.plugin,
            offerId: offer.id,
            options,
        });
        return result.plan;
    }
    const plugin = pluginUIRegistry.get(offer.plugin);
    const provider = plugin?.smartIntake;
    if (!provider) return buildDefaultPlan(bundle);
    return provider.buildPlan({ bundle, pluginName: offer.plugin }, offer.id, options);
}

function buildDefaultPlan(bundle: SmartIntakeBundle): SmartIntakePlan {
    const operations: SmartIntakeOperation[] = [];
    for (const item of bundle.items) {
        if (isAssetItem(item)) {
            const as = `asset_${item.id.replace(/[^a-zA-Z0-9_]/g, "_")}`;
            operations.push({
                type: "commitAsset",
                itemId: item.id,
                targetPathHint: defaultAssetPathHint(item),
                as,
            });
            const label = bundle.selection?.text?.trim() || item.name;
            operations.push({
                type: "insertMarkdown",
                markdown: item.kind === "file" && isImageItem(item)
                    ? `![${escapeMarkdownLabel(label)}]($assetRef(${as}))`
                    : `[${escapeMarkdownLabel(label)}]($assetRef(${as}))`,
                replaceSelection: true,
            });
            continue;
        }
        if (item.kind === "html") {
            const markdown = htmlToMarkdown(item.html, item.text);
            if (markdown) {
                operations.push({ type: "insertMarkdown", markdown, replaceSelection: true });
            }
            continue;
        }
        if (item.kind === "url") {
            operations.push({
                type: "insertMarkdown",
                markdown: `[${escapeMarkdownLabel(item.title ?? item.url)}](${item.url})`,
                replaceSelection: true,
            });
            continue;
        }
        if (item.kind === "json" || item.kind === "har") {
            operations.push({
                type: "insertMarkdown",
                markdown: `\`\`\`json\n${jsonItemText(item)}\n\`\`\``,
                replaceSelection: true,
            });
            continue;
        }
        if (item.kind === "table") {
            operations.push({
                type: "insertMarkdown",
                markdown: tableTextToMarkdown(item.text, item.format),
                replaceSelection: true,
            });
            continue;
        }
        if (item.kind === "magic-fragment") {
            operations.push({
                type: "insertMarkdown",
                markdown: item.markdown,
                replaceSelection: true,
            });
            continue;
        }
        if (item.kind === "text") {
            operations.push({
                type: "insertMarkdown",
                markdown: item.text,
                replaceSelection: true,
            });
        }
    }
    return { operations };
}

async function buildInlineImageFallbackMarkdown(bundle: SmartIntakeBundle): Promise<string | null> {
    const assetItems = bundle.items.filter(isAssetItem);
    const files = assetItems.filter(
        (item): item is SmartIntakeFileItemWithBlob =>
            item.kind === "file" && !!item.blob && isImageItem(item),
    );
    if (files.length === 0 || files.length !== assetItems.length || assetItems.length !== bundle.items.length) {
        return null;
    }
    const parts: string[] = [];
    for (const file of files) {
        const label = bundle.selection?.text?.trim() || file.name || defaultFileName(file.blob);
        const dataUrl = `data:${file.mime || file.blob.type || "application/octet-stream"};base64,${await blobToBase64(file.blob)}`;
        parts.push(`![${escapeMarkdownLabel(label)}](${dataUrl})`);
    }
    return parts.join("\n\n");
}

async function applySmartIntakePlan(
    ctx: IntakeContext,
    plan: SmartIntakePlan,
): Promise<void> {
    const state: ResolvedPlanState = {
        assetRefs: new Map(),
        createdDefinitions: new Map(),
        markdownParts: [],
        openConfigDefinitionId: null,
    };

    for (const operation of plan.operations) {
        switch (operation.type) {
            case "commitAsset": {
                const ref = await getSmartIntakePlatform().commitAsset({
                    bundle: ctx.bundle,
                    operation,
                });
                state.assetRefs.set(operation.as, ref);
                break;
            }
            case "insertMarkdown":
                state.markdownParts.push(resolveTemplate(operation.markdown, state));
                break;
            case "createDefinition": {
                const id = uniqueDefinitionId(ctx.view.state.doc.toString(), operation.idHint);
                state.createdDefinitions.set(operation.as ?? operation.idHint, id);
                state.markdownParts.push(
                    renderDefinitionMarkdown(
                        id,
                        operation.plugin,
                        resolveConfigTemplate(operation.config, state),
                    ),
                );
                break;
            }
            case "createRender":
                state.markdownParts.push(
                    operation.mode === "inline"
                        ? `::render[${resolveDefinitionRef(operation.definitionRef, state)}]{mode="inline"}::`
                        : `::render[${resolveDefinitionRef(operation.definitionRef, state)}]::`,
                );
                break;
            case "openConfig":
            case "selectDefinition":
                state.openConfigDefinitionId = resolveDefinitionRef(operation.definitionRef, state);
                break;
            case "patchDefinitionConfig":
                patchDefinitionConfig(ctx.view, operation, state);
                break;
        }
    }

    if (state.markdownParts.length > 0) {
        insertMarkdown(ctx.view, state.markdownParts.join("\n\n"));
    }
    if (state.openConfigDefinitionId) {
        openDefinitionConfig(state.openConfigDefinitionId);
    }

    if (ctx.bundle.documentPath) {
        const assetsDir = getDocumentAssetsDir(ctx.bundle.documentPath);
        await useEditorStore.getState().refreshSubtree(assetsDir).catch(() => {});
    }
}

async function commitAssetToWorkspace({
    bundle,
    operation,
}: SmartIntakeCommitAssetInput): Promise<string> {
    const item = bundle.items.find((candidate) => candidate.id === operation.itemId);
    if (!item || !isAssetItem(item)) throw new Error(tr("smartIntake.error.assetItemNotFound"));
    if (!bundle.documentPath) throw new Error(tr("smartIntake.error.saveBeforeImport"));

    const assetsDir = getDocumentAssetsDir(bundle.documentPath);
    const relativePath = await uniqueAssetRelativePath(assetsDir, operation.targetPathHint);
    const targetPath = `${assetsDir}/${relativePath}`;

    if (item.stagedPath) {
        const result = await getPlatformService().engine.workspaceCopy({
            from: item.stagedPath,
            to: targetPath,
            autoRename: false,
        });
        if (!result.success) throw new Error(tr("smartIntake.error.saveAssetFailed", { name: item.name }));
        return `$ref{assets/${relativePath}}`;
    }

    if (item.sourcePath) {
        const platform = getSmartIntakePlatform();
        if (platform.copyExternalPathToWorkspace) {
            await platform.copyExternalPathToWorkspace({
                from: item.sourcePath,
                to: targetPath,
            });
            return `$ref{assets/${relativePath}}`;
        }
        const result = await getPlatformService().engine.workspaceCopy({
            from: item.sourcePath,
            to: targetPath,
            autoRename: false,
        });
        if (!result.success) throw new Error(tr("smartIntake.error.saveAssetFailed", { name: item.name }));
        return `$ref{assets/${relativePath}}`;
    }

    if (item.kind !== "file" || !item.blob) {
        throw new Error(tr("smartIntake.error.filePayloadUnavailable"));
    }
    const content = await blobToBase64(item.blob);
    const result = await getPlatformService().engine.workspaceWriteFile({
        path: targetPath,
        content,
        encoding: "base64",
        createParents: true,
    });
    if (!result.success) throw new Error(tr("smartIntake.error.saveAssetFailed", { name: item.name }));
    return `$ref{assets/${relativePath}}`;
}

async function uniqueAssetRelativePath(
    assetsDir: string,
    targetPathHint: string,
): Promise<string> {
    const normalized = sanitizeRelativePath(targetPathHint);
    const slash = normalized.lastIndexOf("/");
    const dir = slash >= 0 ? normalized.slice(0, slash) : "";
    const name = slash >= 0 ? normalized.slice(slash + 1) : normalized;
    const dot = name.lastIndexOf(".");
    const base = dot > 0 ? name.slice(0, dot) : name;
    const ext = dot > 0 ? name.slice(dot) : "";
    let candidate = normalized;
    let counter = 0;
    const engine = getPlatformService().engine;
    while ((await engine.workspaceExists({ path: `${assetsDir}/${candidate}` })).exists) {
        counter++;
        candidate = dir
            ? `${dir}/${base}-${counter}${ext}`
            : `${base}-${counter}${ext}`;
    }
    return candidate;
}

function insertMarkdown(view: EditorView, markdown: string): void {
    if (!markdown.trim()) return;
    view.focus();
    const selection = view.state.selection.main;
    const linePrefix = selection.from > 0 ? "\n\n" : "";
    const lineSuffix = selection.to < view.state.doc.length ? "\n\n" : "";
    view.dispatch({
        changes: {
            from: selection.from,
            to: selection.to,
            insert: `${linePrefix}${markdown}${lineSuffix}`,
        },
        selection: { anchor: selection.from + linePrefix.length + markdown.length },
        scrollIntoView: true,
        userEvent: "input.paste",
    });
    syncViewContentToStore(view);
}

function patchDefinitionConfig(
    view: EditorView,
    operation: Extract<SmartIntakeOperation, { type: "patchDefinitionConfig" }>,
    state: ResolvedPlanState,
): void {
    const definitionId = resolveDefinitionRef(operation.definitionId, state);
    const content = view.state.doc.toString();
    const parsed = parseMagicMd(content);
    const definition = parsed.definitions.get(definitionId);
    if (!definition) throw new Error(tr("smartIntake.error.definitionNotFound", { id: definitionId }));
    const patch = resolveConfigTemplate(operation.patch, state);
    const nextConfig = deepMergeRecord(definition.config, patch);
    const updated = updateDefinitionConfigAndDropOutput(content, definitionId, nextConfig);
    if (updated === content) return;
    const anchor = Math.min(view.state.selection.main.head, updated.length);
    view.dispatch({
        changes: { from: 0, to: view.state.doc.length, insert: updated },
        selection: { anchor },
        scrollIntoView: true,
    });
    syncViewContentToStore(view);
}

function syncViewContentToStore(view: EditorView): void {
    const tabId = view.dom.dataset.magicTabId || useEditorStore.getState().activeTabId;
    useEditorStore.getState().setContent(view.state.doc.toString(), tabId || undefined);
}

function openDefinitionConfig(definitionId: string): void {
    const store = useEditorStore.getState();
    store.setRightSidebarDefinitionId(definitionId);
    if (!store.rightSidebarOpen) store.toggleRightSidebar();
}

function resolveTemplate(value: string, state: ResolvedPlanState): string {
    return value
        .replace(/\$assetRef\(([^)]+)\)/g, (_match, key: string) => {
            return state.assetRefs.get(key) ?? "";
        })
        .replace(/\$created\(([^)]+)\)/g, (_match, key: string) => {
            return state.createdDefinitions.get(key) ?? sanitizeIdentifier(key);
        });
}

function resolveDefinitionRef(value: string, state: ResolvedPlanState): string {
    const createdMatch = value.match(/^\$created\(([^)]+)\)$/);
    if (createdMatch) {
        return state.createdDefinitions.get(createdMatch[1]) ?? sanitizeIdentifier(createdMatch[1]);
    }
    return resolveTemplate(value, state);
}

function resolveConfigTemplate(
    value: Record<string, unknown>,
    state: ResolvedPlanState,
): Record<string, unknown> {
    const resolveValue = (input: unknown): unknown => {
        if (typeof input === "string") return resolveTemplate(input, state);
        if (Array.isArray(input)) return input.map(resolveValue);
        if (input && typeof input === "object") {
            return Object.fromEntries(
                Object.entries(input as Record<string, unknown>).map(([key, nested]) => [
                    key,
                    resolveValue(nested),
                ]),
            );
        }
        return input;
    };
    return resolveValue(value) as Record<string, unknown>;
}

function renderDefinitionMarkdown(
    id: string,
    pluginName: string,
    config: Record<string, unknown>,
): string {
    return [
        `::define[${id}]{plugin="${pluginName}"}`,
        "```config",
        JSON.stringify(config, null, 2),
        "```",
        "::",
    ].join("\n");
}

function uniqueDefinitionId(content: string, idHint: string): string {
    const base = sanitizeIdentifier(idHint || "intake");
    const existing = new Set<string>();
    const re = /::define\[([^\]]+)\]/g;
    let match: RegExpExecArray | null;
    while ((match = re.exec(content))) existing.add(match[1]);
    if (!existing.has(base)) return base;
    let counter = 1;
    while (existing.has(`${base}${counter}`)) counter++;
    return `${base}${counter}`;
}

function defaultAssetPathHint(item: SmartIntakeAssetItem): string {
    const folder =
        item.kind === "folder"
            ? "clipboard/folders"
            : isImageItem(item)
                ? "clipboard/images"
                : "clipboard/files";
    const fallbackName = item.kind === "file" ? defaultFileName(item.blob) : "dropped-folder";
    return `${folder}/${sanitizeRelativePath(item.name || fallbackName)}`;
}

function isAssetItem(item: SmartIntakeContentItem): item is SmartIntakeAssetItem {
    return item.kind === "file" || item.kind === "folder";
}

function hasRuntimeAddressableAsset(bundle: SmartIntakeBundle): boolean {
    return bundle.items.some((item) => {
        if (!isAssetItem(item)) return false;
        return !!(item.sourcePath ?? item.stagedPath);
    });
}

function isImageItem(item: Extract<SmartIntakeContentItem, { kind: "file" }>): boolean {
    if (item.mime && IMAGE_MIME_RE.test(item.mime)) return true;
    const ext = extensionOf(item.name);
    return IMAGE_EXTENSIONS.has(ext);
}

function defaultFileName(file?: Blob): string {
    const mime = file?.type ?? "";
    if (IMAGE_MIME_RE.test(mime)) return `pasted.${mimeToExtension(mime)}`;
    return "pasted-file";
}

function buildFileSignature(file: File, name: string): string {
    const lastModified = Number.isFinite(file.lastModified) ? file.lastModified : 0;
    return [name.replace(/\\/g, "/"), file.size, file.type || "", lastModified].join("\0");
}

function mimeToExtension(mime: string): string {
    switch (mime.toLowerCase()) {
        case "image/jpeg":
            return "jpg";
        case "image/png":
            return "png";
        case "image/gif":
            return "gif";
        case "image/webp":
            return "webp";
        case "image/svg+xml":
            return "svg";
        case "image/avif":
            return "avif";
        default:
            return "bin";
    }
}

function extensionOf(name: string): string {
    const dot = name.lastIndexOf(".");
    return dot >= 0 ? name.slice(dot + 1).toLowerCase() : "";
}

function sanitizeRelativePath(input: string): string {
    const parts = input
        .replace(/\\/g, "/")
        .split("/")
        .map(sanitizeFileName)
        .filter(Boolean);
    return parts.length > 0 ? parts.join("/") : "clipboard/files/pasted-file";
}

function sanitizeFileName(input: string): string {
    const name = input.trim().replace(/[^\w.-]+/g, "-").replace(/^-+|-+$/g, "");
    return name || "pasted-file";
}

function sanitizeIdentifier(input: string): string {
    const id = input
        .replace(/\.[^.]+$/, "")
        .replace(/[^\w-]+/g, "-")
        .replace(/^-+|-+$/g, "");
    return /^[A-Za-z_]/.test(id) ? id : `intake-${id || "item"}`;
}

function escapeMarkdownLabel(value: string): string {
    return value.replace(/[\[\]]/g, "");
}

function safeGetData(data: DataTransfer, type: string): string {
    try {
        return data.getData(type) ?? "";
    } catch {
        return "";
    }
}

function getDataTransferEntry(item: DataTransferItem): BrowserFileSystemEntry | null {
    try {
        return (item as DataTransferItemWithEntry).webkitGetAsEntry?.() ?? null;
    } catch {
        return null;
    }
}

async function readDirectoryEntryFiles(
    entry: BrowserFileSystemEntry,
    relativePath: string,
): Promise<Array<{ file: File; relativePath: string }>> {
    if (entry.isFile) {
        const file = await readFileEntry(entry as BrowserFileSystemFileEntry);
        return file ? [{ file, relativePath }] : [];
    }
    if (!entry.isDirectory) return [];

    const directory = entry as BrowserFileSystemDirectoryEntry;
    const nested = await readDirectoryEntries(directory);
    const files: Array<{ file: File; relativePath: string }> = [];
    for (const child of nested) {
        files.push(
            ...(await readDirectoryEntryFiles(
                child,
                `${relativePath}/${sanitizeFileName(child.name || "item")}`,
            )),
        );
    }
    return files;
}

function readFileEntry(entry: BrowserFileSystemFileEntry): Promise<File | null> {
    return new Promise((resolve) => {
        entry.file(resolve, () => resolve(null));
    });
}

async function readDirectoryEntries(
    entry: BrowserFileSystemDirectoryEntry,
): Promise<BrowserFileSystemEntry[]> {
    const reader = entry.createReader();
    const entries: BrowserFileSystemEntry[] = [];
    for (;;) {
        const batch = await new Promise<BrowserFileSystemEntry[]>((resolve) => {
            reader.readEntries(resolve, () => resolve([]));
        });
        if (batch.length === 0) break;
        entries.push(...batch);
    }
    return entries;
}

function looksLikeMagicFragment(text: string): boolean {
    return /::(?:define|render|content|for|use|import)\b/.test(text);
}

function looksLikeStandaloneUrl(text: string): boolean {
    return /^[A-Za-z][A-Za-z0-9+.-]*:\/\/[^\s<>"'`]+$/i.test(text);
}

function containsCustomSchemeUrl(text: string): boolean {
    const match = text.match(SCHEME_URL_RE)?.[0];
    return !!match && !/^https?:\/\//i.test(match);
}

function parseMarkdownUrl(text: string): { url: string; title?: string } | null {
    const trimmed = text.trim();
    const markdownLink = trimmed.match(/^\[([^\]]*)\]\((https?:\/\/[^)\s]+)\)$/i);
    if (markdownLink) {
        const label = markdownLink[1]?.trim() ?? "";
        const url = markdownLink[2];
        return { url, ...(label && label !== url ? { title: label } : {}) };
    }
    const autolink = trimmed.match(/^<(https?:\/\/[^>\s]+)>$/i);
    return autolink ? { url: autolink[1] } : null;
}

function addPlainTextIntakeItem({
    items,
    plain,
    uriLines,
    nextIndex,
}: {
    items: SmartIntakeContentItem[];
    plain: string;
    uriLines: string[];
    nextIndex: () => number;
}): void {
    if (looksLikeTabularText(plain) && !items.some((item) => item.kind === "table")) {
        const format = plain.includes("\t") ? "tsv" : "csv";
        items.push({
            id: `table-${nextIndex()}`,
            kind: "table",
            format,
            text: plain,
            ...tableRowsFromText(plain, format),
        });
        return;
    }
    const markdownUrl = parseMarkdownUrl(plain);
    if (markdownUrl && !uriLines.includes(markdownUrl.url)) {
        items.push({
            id: `url-${nextIndex()}`,
            kind: "url",
            url: markdownUrl.url,
            ...(markdownUrl.title ? { title: markdownUrl.title } : {}),
        });
        return;
    }
    if (looksLikeStandaloneUrl(plain) && !uriLines.includes(plain)) {
        items.push({ id: `url-${nextIndex()}`, kind: "url", url: plain });
        return;
    }
    if (containsCustomSchemeUrl(plain)) {
        items.push({ id: `text-${nextIndex()}`, kind: "text", format: "plain", text: plain });
    }
}

function looksLikeTabularText(text: string): boolean {
    const lines = text.split(/\r?\n/).filter((line) => line.trim());
    if (lines.length < 2) return false;
    const separator = text.includes("\t") ? "\t" : ",";
    if (!text.includes(separator)) return false;
    const widths = lines.map((line) => splitTableLine(line, separator).length);
    return widths[0] > 1 && widths.every((width) => width === widths[0]);
}

function looksLikeStructuredJsonText(text: string): boolean {
    if (!looksLikeJsonText(text)) return false;
    return parseJsonText(text).ok;
}

function looksLikeJsonText(text: string): boolean {
    const trimmed = text.trim();
    return trimmed.startsWith("{") || trimmed.startsWith("[");
}

function shouldReadStructuredTextFile(name: string, mime: string | undefined, size: number): boolean {
    if (size <= 0 || size > MAX_STRUCTURED_TEXT_FILE_BYTES) return false;
    const ext = extensionOf(name);
    if (["json", "har", "csv", "tsv"].includes(ext)) return true;
    const type = (mime ?? "").toLowerCase();
    return type === "application/json" ||
        type === "text/csv" ||
        type === "text/tab-separated-values" ||
        type === "application/har+json";
}

function addStructuredTextItem(
    items: SmartIntakeContentItem[],
    nextIndex: () => number,
    input: {
        text: string;
        name?: string;
        mime?: string;
        sourceItemId?: string;
    },
): boolean {
    const text = input.text.trim();
    if (!text) return false;
    const parsed = parseJsonText(text);
    if (parsed.ok) {
        if (isHarData(parsed.value)) {
            items.push({
                id: `har-${nextIndex()}`,
                kind: "har",
                data: parsed.value,
                text,
                name: input.name ?? "pasted.har.json",
                mime: input.mime ?? "application/json",
                entryCount: harEntryCount(parsed.value),
                sourceItemId: input.sourceItemId,
            });
            return true;
        }
        items.push({
            id: `json-${nextIndex()}`,
            kind: "json",
            data: parsed.value,
            text,
            name: input.name ?? "pasted.json",
            mime: input.mime ?? "application/json",
            sourceItemId: input.sourceItemId,
        });
        return true;
    }

    const ext = extensionOf(input.name ?? "");
    if ((ext === "csv" || ext === "tsv" || looksLikeTabularText(text)) &&
        !items.some((item) => item.kind === "table" && item.sourceItemId === input.sourceItemId)) {
        const format = ext === "tsv" || text.includes("\t") ? "tsv" : "csv";
        items.push({
            id: `table-${nextIndex()}`,
            kind: "table",
            format,
            text,
            sourceItemId: input.sourceItemId,
            ...tableRowsFromText(text, format),
        });
        return true;
    }
    return false;
}

function parseJsonText(text: string): { ok: true; value: unknown } | { ok: false } {
    if (!looksLikeJsonText(text)) return { ok: false };
    try {
        return { ok: true, value: JSON.parse(text) };
    } catch {
        return { ok: false };
    }
}

function isHarData(value: unknown): boolean {
    if (!isRecord(value)) return false;
    const log = value.log;
    return isRecord(log) && Array.isArray(log.entries);
}

function harEntryCount(value: unknown): number | undefined {
    if (!isRecord(value) || !isRecord(value.log) || !Array.isArray(value.log.entries)) return undefined;
    return value.log.entries.length;
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return value !== null && typeof value === "object" && !Array.isArray(value);
}

function containsDataUrlImage(text: string): boolean {
    DATA_URL_RE.lastIndex = 0;
    return DATA_URL_RE.test(text);
}

function isDataUrl(value: string): boolean {
    return /^data:/i.test(value);
}

function extractDataUrlImagesFromText(text: string): Array<{ blob: Blob; name: string; mime: string }> {
    const images: Array<{ blob: Blob; name: string; mime: string }> = [];
    const matches = text.match(DATA_URL_RE) ?? [];
    matches.forEach((url, index) => {
        const parsed = dataUrlToBlob(url);
        if (parsed) images.push({ ...parsed, name: `pasted-${index + 1}.${mimeToExtension(parsed.mime)}` });
    });
    return images;
}

function extractDataUrlImagesFromHtml(html: string): Array<{ blob: Blob; name: string; mime: string }> {
    if (typeof document === "undefined") return extractDataUrlImagesFromText(html);
    const root = document.createElement("div");
    root.innerHTML = html;
    return Array.from(root.querySelectorAll("img"))
        .map((img, index) => {
            const parsed = dataUrlToBlob(img.getAttribute("src") ?? "");
            if (!parsed) return null;
            const alt = img.getAttribute("alt")?.trim();
            const base = alt ? sanitizeFileName(alt) : `pasted-${index + 1}`;
            return { ...parsed, name: `${base}.${mimeToExtension(parsed.mime)}` };
        })
        .filter((entry): entry is { blob: Blob; name: string; mime: string } => !!entry);
}

function dataUrlToBlob(url: string): { blob: Blob; mime: string } | null {
    const match = url.trim().match(DATA_IMAGE_RE);
    if (!match) return null;
    const mime = match[1].toLowerCase();
    const base64 = match[2].replace(/\s+/g, "");
    try {
        const binary = atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        return { blob: new Blob([bytes], { type: mime }), mime };
    } catch {
        return null;
    }
}

function removeHtmlNodes(
    html: string,
    predicate: (element: Element) => boolean,
): string {
    if (typeof document === "undefined") return html;
    const root = document.createElement("div");
    root.innerHTML = html;
    for (const element of Array.from(root.querySelectorAll("*"))) {
        if (predicate(element)) element.remove();
    }
    return root.innerHTML;
}

function htmlHasMeaningfulContent(html: string): boolean {
    if (!html.trim()) return false;
    if (typeof document === "undefined") return html.trim().length > 0;
    const root = document.createElement("div");
    root.innerHTML = html;
    return !!root.textContent?.trim() || !!root.querySelector("img[src],a[href],pre,code");
}

function extractHtmlTableItems(html: string): string[] {
    if (typeof document === "undefined") return [];
    const root = document.createElement("div");
    root.innerHTML = html;
    return Array.from(root.querySelectorAll("table"))
        .map(htmlTableToMarkdown)
        .filter((table) => table.trim());
}

function htmlToMarkdown(html: string, fallback?: string): string {
    if (typeof document === "undefined") return fallback?.trim() ?? "";
    const root = document.createElement("div");
    root.innerHTML = html;
    const rendered = Array.from(root.childNodes)
        .map((node) => renderHtmlNode(node))
        .join("")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
    return rendered || fallback?.trim() || "";
}

function renderHtmlNode(node: Node): string {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? "";
    if (node.nodeType !== Node.ELEMENT_NODE) return "";
    const element = node as HTMLElement;
    const tag = element.tagName.toLowerCase();
    const children = () => Array.from(element.childNodes).map(renderHtmlNode).join("").trim();
    switch (tag) {
        case "br":
            return "\n";
        case "p":
        case "div":
        case "section":
        case "article":
            return `${children()}\n\n`;
        case "h1":
            return `# ${children()}\n\n`;
        case "h2":
            return `## ${children()}\n\n`;
        case "h3":
            return `### ${children()}\n\n`;
        case "h4":
            return `#### ${children()}\n\n`;
        case "strong":
        case "b":
            return `**${children()}**`;
        case "em":
        case "i":
            return `*${children()}*`;
        case "code":
            return `\`${element.textContent ?? ""}\``;
        case "pre":
            return `\n\`\`\`\n${element.textContent?.trim() ?? ""}\n\`\`\`\n\n`;
        case "blockquote":
            return `${children().split(/\r?\n/).map((line) => `> ${line}`).join("\n")}\n\n`;
        case "a": {
            const href = element.getAttribute("href") ?? "";
            const label = children() || href;
            return href ? `[${escapeMarkdownLabel(label)}](${href})` : label;
        }
        case "img": {
            const src = element.getAttribute("src") ?? "";
            if (!src || isDataUrl(src)) return "";
            const alt = element.getAttribute("alt") ?? "";
            return `![${escapeMarkdownLabel(alt)}](${src})`;
        }
        case "ul":
            return `${Array.from(element.children).map((child) => `- ${renderHtmlNode(child).trim()}`).join("\n")}\n\n`;
        case "ol":
            return `${Array.from(element.children).map((child, index) => `${index + 1}. ${renderHtmlNode(child).trim()}`).join("\n")}\n\n`;
        case "li":
            return children();
        case "table":
            return `${htmlTableToMarkdown(element)}\n\n`;
        default:
            return children();
    }
}

function htmlTableToMarkdown(table: Element): string {
    const rows = Array.from(table.querySelectorAll("tr")).map((row) =>
        Array.from(row.querySelectorAll("th,td")).map((cell) => normalizeTableCell(cell.textContent ?? "")),
    ).filter((row) => row.length > 0);
    if (rows.length === 0) return "";
    const width = Math.max(...rows.map((row) => row.length));
    const normalized = rows.map((row) => padRow(row, width));
    return rowsToMarkdownTable(normalized);
}

function tableTextToMarkdown(text: string, format: "csv" | "tsv" | "html"): string {
    if (format === "html") return text.trim();
    const separator = format === "tsv" ? "\t" : ",";
    const rows = text
        .split(/\r?\n/)
        .filter((line) => line.trim())
        .map((line) => splitTableLine(line, separator).map(normalizeTableCell));
    if (rows.length === 0) return "";
    const width = Math.max(...rows.map((row) => row.length));
    return rowsToMarkdownTable(rows.map((row) => padRow(row, width)));
}

function tableRowsFromText(
    text: string,
    format: "csv" | "tsv" | "html",
): { columns?: string[]; rows?: Record<string, unknown>[] } {
    const rows = format === "html"
        ? parseMarkdownTableRows(text)
        : text
            .split(/\r?\n/)
            .filter((line) => line.trim())
            .map((line) => splitTableLine(line, format === "tsv" ? "\t" : ",").map((cell) => cell.trim()));
    if (rows.length < 2) return {};
    const [header, ...body] = rows;
    const columns = dedupeColumns(header.map((cell, index) => cell || `column_${index + 1}`));
    const records = body
        .filter((row) => row.some((cell) => cell.trim()))
        .map((row) => Object.fromEntries(columns.map((column, index) => [column, row[index] ?? ""])));
    if (records.length === 0) return { columns };
    return { columns, rows: records };
}

function parseMarkdownTableRows(text: string): string[][] {
    return text
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter((line) => line.startsWith("|") && line.endsWith("|"))
        .filter((line, index) => index !== 1 || !/^\|\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|$/.test(line))
        .map((line) => line.slice(1, -1).split("|").map((cell) => cell.trim().replace(/\\\|/g, "|")));
}

function dedupeColumns(columns: string[]): string[] {
    const seen = new Map<string, number>();
    return columns.map((column, index) => {
        const base = sanitizeColumnName(column) || `column_${index + 1}`;
        const count = seen.get(base) ?? 0;
        seen.set(base, count + 1);
        return count === 0 ? base : `${base}_${count + 1}`;
    });
}

function sanitizeColumnName(value: string): string {
    return value.trim().replace(/\s+/g, "_") || "column";
}

function jsonItemText(item: Extract<SmartIntakeContentItem, { kind: "json" | "har" }>): string {
    try {
        return JSON.stringify(item.data, null, 2);
    } catch {
        return item.text?.trim() || String(item.data);
    }
}

function splitTableLine(line: string, separator: string): string[] {
    if (separator === "\t") return line.split("\t");
    const cells: string[] = [];
    let current = "";
    let quoted = false;
    for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') {
            quoted = !quoted;
            continue;
        }
        if (ch === "," && !quoted) {
            cells.push(current);
            current = "";
            continue;
        }
        current += ch;
    }
    cells.push(current);
    return cells;
}

function rowsToMarkdownTable(rows: string[][]): string {
    const [header, ...body] = rows;
    return [
        `| ${header.join(" | ")} |`,
        `| ${header.map(() => "---").join(" | ")} |`,
        ...body.map((row) => `| ${row.join(" | ")} |`),
    ].join("\n");
}

function padRow(row: string[], width: number): string[] {
    return [...row, ...Array.from({ length: Math.max(0, width - row.length) }, () => "")];
}

function normalizeTableCell(value: string): string {
    return value.trim().replace(/\|/g, "\\|").replace(/\s+/g, " ");
}

async function blobToBase64(blob: Blob): Promise<string> {
    const buffer = new Uint8Array(await blob.arrayBuffer());
    let binary = "";
    const chunkSize = 0x8000;
    for (let i = 0; i < buffer.length; i += chunkSize) {
        binary += String.fromCharCode(...buffer.subarray(i, i + chunkSize));
    }
    return btoa(binary);
}

function documentPathToBundlePath(documentPath: string): string {
    const assetsDir = getDocumentAssetsDir(documentPath);
    return assetsDir.replace(/\/assets$/, "");
}

function deepMergeRecord(
    base: Record<string, unknown>,
    patch: Record<string, unknown>,
): Record<string, unknown> {
    const output = { ...base };
    for (const [key, value] of Object.entries(patch)) {
        if (
            value &&
            typeof value === "object" &&
            !Array.isArray(value) &&
            output[key] &&
            typeof output[key] === "object" &&
            !Array.isArray(output[key])
        ) {
            output[key] = deepMergeRecord(
                output[key] as Record<string, unknown>,
                value as Record<string, unknown>,
            );
        } else {
            output[key] = value;
        }
    }
    return output;
}

function chooseSmartIntakeOffer(
    offers: ResolvedSmartIntakeOffer[],
): Promise<ResolvedSmartIntakeOffer | null> {
    if (offers.length <= 1 || typeof document === "undefined") {
        return Promise.resolve(offers[0] ?? null);
    }

    return new Promise((resolve) => {
        const overlay = document.createElement("div");
        overlay.className = "fixed inset-0 flex items-center justify-center bg-black/30 px-4";
        overlay.style.zIndex = "2147483647";

        const dialog = document.createElement("div");
        dialog.className =
            "w-full max-w-md overflow-hidden rounded-lg border border-border bg-popover text-popover-foreground shadow-2xl";
        dialog.setAttribute("role", "dialog");
        dialog.setAttribute("aria-modal", "true");

        const header = document.createElement("div");
        header.className = "border-b border-border px-3 py-2";
        const title = document.createElement("div");
        title.className = "text-sm font-semibold";
        title.textContent = tr("smartIntake.title");
        header.append(title);
        dialog.appendChild(header);

        const list = document.createElement("div");
        list.className = "max-h-[320px] overflow-y-auto p-1.5";
        for (const offer of offers) {
            const button = document.createElement("button");
            button.type = "button";
            button.className =
                "group flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
            const icon = document.createElement("span");
            icon.className =
                "flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-border bg-background text-xs";
            icon.textContent = offer.icon ?? "↵";
            const body = document.createElement("span");
            body.className = "flex min-w-0 flex-1 items-center justify-between gap-3";
            const source = document.createElement("span");
            source.className = "min-w-0 truncate text-xs font-medium";
            source.textContent = smartIntakeOfferSourceLabel(offer);
            const action = document.createElement("span");
            action.className = "max-w-[58%] truncate text-xs text-muted-foreground";
            action.textContent = offer.title;
            body.append(source, action);
            button.append(icon, body);
            button.addEventListener("click", () => {
                cleanup();
                resolve(offer);
            });
            list.appendChild(button);
        }
        dialog.appendChild(list);

        const footer = document.createElement("div");
        footer.className = "flex justify-end border-t border-border bg-muted/30 px-3 py-2";
        const cancel = document.createElement("button");
        cancel.type = "button";
        cancel.className =
            "rounded-md px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
        cancel.textContent = tr("common.cancel");
        cancel.addEventListener("click", () => {
            cleanup();
            resolve(null);
        });
        footer.appendChild(cancel);
        dialog.appendChild(footer);

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                cleanup();
                resolve(null);
            }
        };
        const cleanup = () => {
            document.removeEventListener("keydown", onKeyDown);
            overlay.remove();
        };
        overlay.addEventListener("click", (event) => {
            if (event.target === overlay) {
                cleanup();
                resolve(null);
            }
        });
        document.addEventListener("keydown", onKeyDown);
        overlay.appendChild(dialog);
        document.body.appendChild(overlay);
        list.querySelector("button")?.focus();
    });
}

function smartIntakeOfferSourceLabel(offer: ResolvedSmartIntakeOffer): string {
    return offer.pluginTitle?.trim() ||
        (offer.plugin ? pluginUIRegistry.get(offer.plugin)?.manifest.title?.trim() : "") ||
        offer.plugin ||
        tr("smartIntake.source.builtin");
}

function requestSmartIntakeOptions(
    offer: ResolvedSmartIntakeOffer,
): Promise<unknown | null> {
    if (!offer.inputSchema?.properties || typeof document === "undefined") {
        return Promise.resolve({});
    }
    const properties = offer.inputSchema.properties;
    const required = new Set(offer.inputSchema.required ?? []);

    return new Promise((resolve) => {
        const overlay = document.createElement("div");
        overlay.className = "fixed inset-0 flex items-center justify-center bg-black/30 px-4";
        overlay.style.zIndex = "2147483647";

        const dialog = document.createElement("form");
        dialog.className =
            "w-full max-w-md overflow-hidden rounded-lg border border-border bg-popover text-popover-foreground shadow-2xl";
        dialog.setAttribute("role", "dialog");
        dialog.setAttribute("aria-modal", "true");

        const header = document.createElement("div");
        header.className = "border-b border-border px-4 py-3";
        const title = document.createElement("div");
        title.className = "text-sm font-semibold";
        title.textContent = offer.title;
        header.appendChild(title);
        if (offer.description) {
            const desc = document.createElement("div");
            desc.className = "mt-0.5 text-xs text-muted-foreground";
            desc.textContent = offer.description;
            header.appendChild(desc);
        }
        dialog.appendChild(header);

        const body = document.createElement("div");
        body.className = "space-y-3 p-4";
        const fields = new Map<string, HTMLInputElement | HTMLSelectElement>();
        for (const [key, schema] of Object.entries(properties)) {
            if (!schema) continue;
            const label = document.createElement("label");
            label.className = "block space-y-1";
            const labelText = document.createElement("span");
            labelText.className = "text-xs font-medium text-muted-foreground";
            labelText.textContent = required.has(key) ? `${key} *` : key;
            label.appendChild(labelText);

            let input: HTMLInputElement | HTMLSelectElement;
            if (schema.enum?.length) {
                const select = document.createElement("select");
                select.className =
                    "w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring";
                for (const value of schema.enum) {
                    const option = document.createElement("option");
                    option.value = String(value);
                    option.textContent = String(value);
                    select.appendChild(option);
                }
                select.value = String(schema.default ?? schema.enum[0] ?? "");
                input = select;
            } else {
                const field = document.createElement("input");
                field.className =
                    "w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring";
                field.type = schemaHasType(schema, "number")
                    ? "number"
                    : schemaHasType(schema, "boolean")
                        ? "checkbox"
                        : "text";
                if (schema.default !== undefined) {
                    if (field.type === "checkbox") field.checked = Boolean(schema.default);
                    else field.value = String(schema.default);
                }
                input = field;
            }
            if (schema.description) {
                const help = document.createElement("span");
                help.className = "block text-xs text-muted-foreground";
                help.textContent = schema.description;
                label.append(input, help);
            } else {
                label.appendChild(input);
            }
            fields.set(key, input);
            body.appendChild(label);
        }
        dialog.appendChild(body);

        const footer = document.createElement("div");
        footer.className = "flex justify-end gap-2 border-t border-border bg-muted/30 px-3 py-2";
        const cancel = document.createElement("button");
        cancel.type = "button";
        cancel.className =
            "rounded-md px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground";
        cancel.textContent = tr("common.cancel");
        cancel.addEventListener("click", () => {
            cleanup();
            resolve(null);
        });
        const submit = document.createElement("button");
        submit.type = "submit";
        submit.className =
            "rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90";
        submit.textContent = tr("smartIntake.action.apply");
        footer.append(cancel, submit);
        dialog.appendChild(footer);

        const cleanup = () => overlay.remove();
        dialog.addEventListener("submit", (event) => {
            event.preventDefault();
            const values: Record<string, unknown> = {};
            for (const [key, input] of fields) {
                if (input instanceof HTMLInputElement && input.type === "checkbox") {
                    values[key] = input.checked;
                    continue;
                }
                const raw = input.value;
                if (required.has(key) && !raw.trim()) return;
                const schema = properties[key];
                values[key] = schemaHasType(schema, "number") ? Number(raw) : raw;
            }
            cleanup();
            resolve(values);
        });
        overlay.addEventListener("click", (event) => {
            if (event.target === overlay) {
                cleanup();
                resolve(null);
            }
        });
        overlay.appendChild(dialog);
        document.body.appendChild(overlay);
        fields.values().next().value?.focus();
    });
}
